import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { INITIAL_SIGNUP_STATE, type SignupState, type SignupStep } from './types';

type SignupContextValue = {
  state: SignupState;
  setField: <K extends keyof SignupState>(key: K, value: SignupState[K]) => void;
  step: SignupStep;
  setStep: (step: SignupStep) => void;
};

const SignupContext = createContext<SignupContextValue | null>(null);

export function SignupProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SignupState>(INITIAL_SIGNUP_STATE);
  const [step, setStep] = useState<SignupStep>('profile');

  const setField = useCallback(<K extends keyof SignupState>(key: K, value: SignupState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const value = useMemo<SignupContextValue>(
    () => ({ state, setField, step, setStep }),
    [state, setField, step],
  );

  return <SignupContext.Provider value={value}>{children}</SignupContext.Provider>;
}

export function useSignup(): SignupContextValue {
  const ctx = useContext(SignupContext);
  if (!ctx) {
    throw new Error('useSignup must be used inside <SignupProvider>');
  }
  return ctx;
}
